import UploadCvIcon from '@core/components/ApplyDialogV2/assets/UploadCvIcon'
import type { FileError } from '@core/app/types'
import React, { type FC, useRef, useState, useCallback, useEffect } from 'react'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import FileUploaderV2ListItem from '@core/components/ApplyDialogV2/FileUploaderV2ListItem'
import './FileUploaderV2.scss'

const baseClass = 'FileUploaderV2'

type FileWithStatus = {
    file: File
    status: 'uploading' | 'success' | 'error'
    error?: string
    progress?: number
}

interface IFileUploaderV2Props {
    disabled?: boolean
    onClick?: () => void
    onDragOver?: () => void
    onDragLeave?: () => void
    onHandleDrop?: (files: File[]) => void
    onChange?: (files: File[]) => void
    onError?: (errors: FileError[]) => void
    multiple?: boolean
    multipleLimit?: number
    accept?: string[]
    maxFileSize?: number
    files?: File[]
}

const FileUploaderV2: FC<IFileUploaderV2Props> = ({
    disabled = false,
    onChange = () => { },
    onHandleDrop = () => { },
    onDragOver = () => { },
    onDragLeave = () => { },
    onClick = () => { },
    onError = () => { },
    multiple = false,
    multipleLimit = 1,
    accept = ['pdf', 'doc', 'docx'],
    maxFileSize = 10 * 1024 * 1024,
    files = [],
}) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [fileItems, setFileItems] = useState<FileWithStatus[]>([])
    const [dragCounter, setDragCounter] = useState(0)
    const [dragging, setDragging] = useState(false)
    const [hovering, setHovering] = useState(false)

    // useEffect(() => {
    //     if (files.length === 0) {
    //         setFileItems([])
    //         return
    //     }
    //
    //     setFileItems((prev) => {
    //         const isSame =
    //             prev.length === files.length &&
    //             prev.every((item, index) => {
    //                 const f = files[index]
    //                 return item.file.name === f.name && item.file.size === f.size
    //             })
    //
    //         if (isSame) return prev
    //
    //         return files.map((f) => ({
    //             file: f,
    //             status: 'success' as const,
    //             progress: 100,
    //         }))
    //     })
    // }, [files])

    const acceptString = accept.map((ext) => (ext.includes('/') ? ext : '.' + ext)).join(',')

    const addFiles = (newFiles: File[]) => {
        const newFileItems = newFiles.map((file) => ({
            file,
            status: 'uploading' as const,
            progress: 0,
        }))
        setFileItems((prev) => [...prev, ...newFileItems])
    }

    const validateFilesSize = (files: File[]): boolean => {
        const errors: { file: File; error: string }[] = []

        for (const file of files) {
            if (maxFileSize && file.size > maxFileSize) {
                errors.push({
                    file,
                    error: `File "${file.name}" is too large. Max size is ${maxFileSize / 1024 / 1024} MB.`,
                })
            }
        }

        if (errors.length > 0) {
            onError(errors)
            return false
        }
        return true
    }

    const validateFilesCount = (files: File[]): File[] => {
        if (!multiple) {
            return files.slice(0, 1)
        }
        if (multipleLimit && files.length > multipleLimit) {
            onError([
                {
                    file: null as any,
                    error: `You can upload max ${multipleLimit} files`,
                },
            ])
            return files.slice(0, multipleLimit)
        }
        return files
    }

    const handleClick = useCallback(() => {
        if (!disabled && inputRef.current) {
            inputRef.current.click()
        }
        onClick()
    }, [disabled, onClick])

    const handleDragEnter = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            if (disabled) return
            setDragCounter((c) => c + 1)
            setDragging(true)
            onDragOver()
        },
        [disabled, onDragOver]
    )

    const handleDragLeave = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            if (disabled) return
            setDragCounter((c) => {
                const newCount = c - 1
                if (newCount <= 0) {
                    setDragging(false)
                    onDragLeave()
                    return 0
                }
                return newCount
            })
        },
        [disabled, onDragLeave]
    )

    const removeFile = (fileToRemove: File) => {
        setFileItems((items) => items.filter((item) => item.file !== fileToRemove))
    }

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            if (disabled) return
            setDragCounter(0)
            setDragging(false)

            let droppedFiles = Array.from(e.dataTransfer.files)

            droppedFiles = validateFilesCount(droppedFiles)

            if (!validateFilesSize(droppedFiles)) {
                return
            }

            if (droppedFiles.length > 0) {
                addFiles(droppedFiles)
                onHandleDrop(droppedFiles)
                onChange(droppedFiles)
            }
        },
        [disabled, multiple, multipleLimit, maxFileSize, onError, onHandleDrop, onChange]
    )

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let files = Array.from(e.target.files || [])

            files = validateFilesCount(files)

            if (!validateFilesSize(files)) {
                e.target.value = ''
                return
            }

            if (files.length > 0) {
                onError([])
                onChange(files)
            }

            e.target.value = ''
        },
        [multiple, multipleLimit, maxFileSize, onChange, onError]
    )

    const getStateClass = () => {
        if (disabled) return 'disabled'
        if (dragging) return 'dragging'
        if (hovering) return 'hover'
        return 'default'
    }

    return (
        <div
            className={`${baseClass} ${getStateClass()}`}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onMouseEnter={() => !disabled && setHovering(true)}
            onMouseLeave={() => !disabled && setHovering(false)}
            tabIndex={0}
            role="button"
            aria-disabled={disabled}
            onKeyDown={(e) => {
                if (disabled) return
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleClick()
                }
            }}
        >
            <input
                type="file"
                accept={acceptString}
                ref={inputRef}
                onChange={handleChange}
                disabled={disabled}
                className={`${baseClass}__input`}
                multiple={multiple}
            />
            <div className={`${baseClass}__content`}>
                <UploadCvIcon className={`${baseClass}__icon`} />
                <p>{dragging ? 'Release file to upload' : 'Drop CV here or click to upload'}</p>
                <span>{accept.join(' / ').toUpperCase()}</span>
            </div>
        </div>
    )
}

export default FileUploaderV2
